import { faFileSignature, faUser, faFileAlt, faSyringe } from '@fortawesome/pro-regular-svg-icons';
import Agreements from 'components/Agreements';
import Forms from 'components/Forms/Forms';
import VaccineApprovals from 'components/VaccineApprovals';
import ClientsComponent from 'components/Clients';
import PageSidebar from 'components/PageSidebar';
import CryptoJS from 'crypto-js';
import AES from 'crypto-js/aes';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import Client from './Client';
import PrivatePage from './PrivatePage';
import { useHasPremiumAccess } from 'hooks';
import usePermissions from 'hooks/usePermissions';
import { AgreementsPath, ClientsPath, FormsPath, SoldMembershipsPath, VaccineApprovalsPath } from 'routes/Paths';
import PremiumFeatureAlert from 'components/UI/PremiumFeatureAlert';
import { useHistory } from 'react-router-dom';
import { faStars } from '@fortawesome/pro-regular-svg-icons';
import SoldMemberships from 'components/Memberships/SoldMemberships/SoldMemberships';

interface MatchParams {
    hash?: string;
}

type ClientsProps = RouteComponentProps<MatchParams>;

interface EditData {
    clientId?: number;
    petId?: number;
}

const ClientsItem = {
    icon: faUser,
    title: 'Clients',
    key: 'clients'
};

const AgreementsItem = {
    icon: faFileSignature,
    title: 'Agreements',
    key: 'agreements'
};

const FormsItem = {
    icon: faFileAlt,
    title: 'Forms',
    key: 'forms'
};

const VaccineApprovalsItem = {
    icon: faSyringe,
    title: 'Vaccine Approvals',
    key: 'vaccine-approvals'
};

const MembershipsItem = {
    icon: faStars,
    title: 'Sold Memberships',
    key: 'memberships'
};

const agreementsEnabled = process.env.REACT_APP_AGREEMENTS_ENABLED === 'true';

const secret = `${process.env.REACT_APP_GLOBAL_SECRET}`;

interface ClientNavigationState {
    returnTo?: string;
    conversationId?: number;
}

const isInternalPath = (path: unknown): path is string =>
    typeof path === 'string' && path.startsWith('/') && !path.startsWith('//');

const convertHash = (hash: string | undefined, secret: string): EditData | null => {
    if (hash) {
        const json: EditData = JSON.parse(AES.decrypt(decodeURIComponent(hash), secret).toString(CryptoJS.enc.Utf8));

        if (json.clientId === undefined && json.petId === undefined) {
            return null;
        } else {
            return json;
        }
    } else {
        return null;
    }
};

export const Clients: FunctionComponent<ClientsProps> = props => {
    const hasReportsPremiumAccess = useHasPremiumAccess('agreements');
    const permissions = usePermissions();
    const history = useHistory();
    const [selectedKey, setSelectedKey] = useState<string>(ClientsItem.key);
    const [editData, setEditData] = useState<EditData | null>(convertHash(props.match.params.hash, secret));

    const sidebarItems = [ClientsItem, AgreementsItem, MembershipsItem, FormsItem, VaccineApprovalsItem].filter(
        item => {
            if (item.key === AgreementsItem.key) {
                return agreementsEnabled && permissions.clients.agreements;
            }

            if (item.key === MembershipsItem.key) {
                return permissions.memberships.viewSold;
            }

            return true;
        }
    );

    useEffect(() => {
        setEditData(convertHash(props.match.params.hash, secret));
    }, [props.match.params.hash]);

    useEffect(() => {
        switch (location.hash) {
            case '#forms':
                setSelectedKey(FormsItem.key);
                break;
            case '#vaccine-approvals':
                setSelectedKey(VaccineApprovalsItem.key);
                break;
            case '#agreements':
                setSelectedKey(
                    agreementsEnabled && permissions.clients.agreements ? AgreementsItem.key : ClientsItem.key
                );
                break;
            case '#sold-memberships':
                setSelectedKey(MembershipsItem.key);
                break;
            default:
                setSelectedKey(ClientsItem.key);
        }

        if (location.hash === '#create') {
            setEditData({});
        }
    }, [location.hash, permissions.clients.agreements]);

    const editHandler = (clientId?: number, petId?: number) => {
        setEditData({ clientId, petId });
    };

    const sidebarKeyToPath: Record<string, string> = {
        [ClientsItem.key]: ClientsPath,
        [AgreementsItem.key]: AgreementsPath,
        [MembershipsItem.key]: SoldMembershipsPath,
        [FormsItem.key]: FormsPath,
        [VaccineApprovalsItem.key]: VaccineApprovalsPath
    };

    const handleSidebarSelect = (key: string) => {
        setSelectedKey(key);
        const path = sidebarKeyToPath[key];
        if (path) {
            history.push(path);
        }
    };

    const sidebar = (
        <PageSidebar
            title="Clients"
            items={sidebarItems}
            selectedKey={selectedKey}
            onSelect={handleSidebarSelect}
        />
    );

    const clients = (
        <PrivatePage
            title="Clients - Cuddles"
            pageName="/clients"
            pageSidebar={sidebar}
        >
            <ClientsComponent
                onAddClient={() => editHandler()}
                onEditClient={editHandler}
                onEditPet={editHandler}
            />
        </PrivatePage>
    );

    const closePremiumModalHandler = () => {
        setSelectedKey(ClientsItem.key);
        history.push('/clients');
    };

    const forms = (
        <PrivatePage
            title="Forms - Cuddles"
            pageName="/clients#forms"
            pageSidebar={sidebar}
        >
            <Forms />
        </PrivatePage>
    );

    const vaccineApprovals = (
        <PrivatePage
            title="Vaccine Approvals - Cuddles"
            pageName={VaccineApprovalsPath}
            pageSidebar={sidebar}
        >
            <VaccineApprovals />
        </PrivatePage>
    );

    const agreements = (
        <PrivatePage
            title="Agreements - Cuddles"
            pageName={AgreementsPath}
            pageSidebar={sidebar}
        >
            {hasReportsPremiumAccess ? (
                <Agreements />
            ) : (
                <PremiumFeatureAlert
                    text={
                        <>
                            Keep clients informed of your policies with Agreements. Create custom agreements, send via
                            email or text, and automate signature collection.
                            <b> Upgrade now</b> and ditch paper agreements.
                        </>
                    }
                    isOpen={true}
                    onCancel={closePremiumModalHandler}
                />
            )}
        </PrivatePage>
    );

    const memberships = (
        <PrivatePage
            title="Memberships - Cuddles"
            pageName={SoldMembershipsPath}
            pageSidebar={sidebar}
        >
            <SoldMemberships />
        </PrivatePage>
    );

    const closeClientHandler = () => {
        const state = props.location.state as ClientNavigationState | null | undefined;
        const returnTo = state?.returnTo;

        if (isInternalPath(returnTo)) {
            history.push(
                returnTo,
                typeof state?.conversationId === 'number' ? { conversationId: state.conversationId } : undefined
            );
            return;
        }

        setEditData(null);
    };

    const client = (
        <Client
            {...editData}
            onClose={closeClientHandler}
        />
    );

    switch (selectedKey) {
        case FormsItem.key:
            return forms;
        case VaccineApprovalsItem.key:
            return vaccineApprovals;
        case AgreementsItem.key:
            return agreements;
        case MembershipsItem.key:
            return memberships;
        default:
            return editData === null ? clients : client;
    }
};

export default Clients;
